import type { MerchantBranding } from '@shared/types';

const DARK_DEFAULTS = {
  background_color: '#0a0a0f',
  card_background_color: '#111118',
  text_color: '#e4e4e7',
  border_color: '#27272a',
  primary_color: '#3b82f6',
  accent_color: '#3b82f6',
  error_color: '#ef4444',
  success_color: '#22c55e',
};

const LIGHT_DEFAULTS = {
  background_color: '#f4f4f5',
  card_background_color: '#ffffff',
  text_color: '#18181b',
  border_color: '#d4d4d8',
  primary_color: '#2563eb',
  accent_color: '#2563eb',
  error_color: '#dc2626',
  success_color: '#16a34a',
};

const RADIUS_MAP: Record<string, string> = {
  none: '0px',
  small: '4px',
  medium: '8px',
  large: '16px',
};

const LOGO_SIZES: Record<string, number> = {
  small: 32,
  medium: 56,
  large: 80,
  xlarge: 112,
};

export { DARK_DEFAULTS, LIGHT_DEFAULTS, LOGO_SIZES };

function toFontStack(font: string | undefined | null): string {
  const DEFAULT = '"IBM Plex Sans", system-ui, sans-serif';
  if (!font || typeof font !== 'string') return DEFAULT;
  const trimmed = font.trim();
  if (!trimmed) return DEFAULT;
  if (trimmed.includes(',')) return trimmed;
  const needsQuotes = /\s/.test(trimmed) || /[^a-zA-Z0-9-]/.test(trimmed);
  const alreadyQuoted =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"));
  const value = alreadyQuoted ? trimmed : needsQuotes ? `"${trimmed}"` : trimmed;
  return `${value}, "IBM Plex Sans", system-ui, sans-serif`;
}

export function buildThemeVars(branding: MerchantBranding): Record<string, string> {
  const defaults = branding.checkout_theme === 'light' ? LIGHT_DEFAULTS : DARK_DEFAULTS;

  return {
    '--bp-bg': branding.background_color || defaults.background_color,
    '--bp-card-bg': branding.card_background_color || defaults.card_background_color,
    '--bp-text': branding.text_color || defaults.text_color,
    '--bp-text-secondary': branding.checkout_theme === 'light' ? '#71717a' : '#a1a1aa',
    '--bp-border': branding.border_color || defaults.border_color,
    '--bp-primary': branding.primary_color || defaults.primary_color,
    '--bp-accent': branding.accent_color || defaults.accent_color,
    '--bp-error': branding.error_color || defaults.error_color,
    '--bp-success': branding.success_color || defaults.success_color,
    '--bp-radius': RADIUS_MAP[branding.corner_radius || 'medium'] || '8px',
    '--bp-heading-font': toFontStack(branding.heading_font),
    '--bp-body-font': toFontStack(branding.body_font),
  };
}

export function buildBackgroundStyle(branding: MerchantBranding): React.CSSProperties {
  const isDark = branding.checkout_theme !== 'light';
  const fallback = branding.background_color || (isDark ? '#0a0a0f' : '#f4f4f5');

  if (branding.background_type === 'gradient') {
    const start = branding.background_gradient_start || (isDark ? '#1a1a2e' : '#e2e8f0');
    const end = branding.background_gradient_end || (isDark ? '#16213e' : '#f8fafc');
    const direction = branding.background_gradient_direction || 'to bottom';
    return { background: `linear-gradient(${direction}, ${start}, ${end})` };
  }

  if (branding.background_type === 'image' && branding.background_image_url) {
    const size = branding.background_image_size || 'cover';
    return {
      backgroundImage: `url(${branding.background_image_url})`,
      backgroundSize: size === 'repeat' ? 'auto' : size,
      backgroundPosition: 'center',
      backgroundRepeat: size === 'repeat' ? 'repeat' : 'no-repeat',
      backgroundAttachment: 'fixed',
    };
  }

  return { backgroundColor: fallback };
}

export function getLogoHeight(branding: MerchantBranding): number {
  if (branding.logo_size === 'custom') {
    return Math.min(200, Math.max(20, branding.logo_custom_height || 56));
  }
  return LOGO_SIZES[branding.logo_size || 'medium'] || 56;
}
